import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronRight, Download, FilePlus2, FolderPlus, HardDrive, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAdmin } from "@/context/AdminContext";
import { api, errText } from "@/lib/api";
import { LEVELS, levelOf } from "@/lib/access";
import { FolderIcon } from "@/components/files/FolderIcon";
import { date, iconFor, size, tool } from "@/lib/files";

const field = "w-full border border-black/20 bg-white px-3 py-2.5 text-sm outline-none focus:border-[#0A0A0A]";
const submitBtn = "flex w-full items-center justify-center gap-2 bg-[#0A0A0A] px-5 py-3 text-sm font-bold text-white hover:bg-[#E62314] disabled:opacity-50";

const LevelPicker = ({ value, onChange, testId }) => {
  const { user } = useAdmin();
  const mine = LEVELS.filter((l) => (user?.levels || []).includes(l.key));
  return (
    <label className="block"><span className="mb-1 block text-xs font-bold uppercase tracking-wider text-[#57534E]">Level</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={field} data-testid={testId}>
        {mine.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
      </select>
    </label>
  );
};

const useFirstLevel = () => {
  const { user } = useAdmin();
  return LEVELS.find((l) => (user?.levels || []).includes(l.key))?.key || "general";
};

export const FolderRequest = ({ open, onOpenChange, parent, onDone }) => {
  const first = useFirstLevel();
  const [f, setF] = useState({ name: "", notes: "", level: first });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ name: "", notes: "", level: parent?.level || first }); return undefined; }, [open, parent, first]);
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Give the folder a name");
    setBusy(true);
    try { await api.post("/requests/folder", { ...f, parent_id: parent?.id || null }); toast.success("Request sent. Pending approval"); onDone(); onOpenChange(false); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-[#FBF9F5] sm:max-w-md" data-testid="folder-request-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Request new folder</DialogTitle><DialogDescription>{parent ? `Inside “${parent.name}”. ` : ""}It will appear once approved.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <input value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Folder name" className={field} data-testid="folder-request-name" />
          {!parent && <LevelPicker value={f.level} onChange={(level) => setF({ ...f, level })} testId="folder-request-level" />}
          <textarea value={f.notes} rows={2} maxLength={1000} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Notes (optional)" className={field} data-testid="folder-request-notes" />
          <button onClick={submit} disabled={busy} className={submitBtn} data-testid="folder-request-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send request</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FileRequest = ({ open, onOpenChange, folder, onDone }) => {
  const first = useFirstLevel();
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState("");
  const [level, setLevel] = useState(first);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setNotes(""); setLevel(first); } return undefined; }, [open, first]);
  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("notes", notes);
      if (folder) fd.append("folder_id", folder.id); else fd.append("level", level);
      await api.post("/requests/upload", fd); toast.success("Request sent. Pending approval"); onDone(); onOpenChange(false);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-[#FBF9F5] sm:max-w-md" data-testid="file-request-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold">Request new file</DialogTitle><DialogDescription>{folder ? `Into “${folder.name}”. ` : ""}It will appear once approved.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3 border border-black/20 bg-white px-3 py-2">
            <button type="button" onClick={() => ref.current?.click()} className="flex shrink-0 items-center gap-1.5 text-sm font-semibold hover:text-[#E62314]" data-testid="file-request-choose"><FilePlus2 className="h-4 w-4" />{file ? "Change" : "Choose file"}</button>
            <span className="min-w-0 truncate text-sm text-[#57534E]" data-testid="file-request-filename">{file ? file.name : "No file chosen"}</span>
            <input ref={ref} type="file" hidden onChange={(e) => { setFile(e.target.files?.[0] || null); e.target.value = ""; }} data-testid="file-request-input" />
          </div>
          {!folder && <LevelPicker value={level} onChange={setLevel} testId="file-request-level" />}
          <textarea value={notes} rows={2} maxLength={1000} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className={field} data-testid="file-request-notes" />
          <button onClick={submit} disabled={busy || !file} className={submitBtn} data-testid="file-request-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Send request</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FileRow = ({ f, showLevel, onDownload }) => {
  const [Icon, color] = iconFor(f);
  return (
    <tr className="border-b border-black/5 hover:bg-[#E8F0FE]" data-testid={`portal-file-${f.id}`}>
      <td className="py-2.5"><span className="flex items-start gap-3 font-semibold"><Icon className={`mt-0.5 h-5 w-5 shrink-0 ${color}`} />
        <span className="min-w-0"><button onClick={() => onDownload(f)} className="block break-words text-left [hyphens:none] hover:underline">{f.name}</button>
          {showLevel && <span className="block text-[11px] font-normal text-[#57534E]" data-testid={`portal-file-${f.id}-level`}>{levelOf(f.level).label}</span>}</span></span></td>
      <td className="hidden py-2.5 text-[#57534E] sm:table-cell">{size(f.size)}</td>
      <td className="hidden py-2.5 text-[#57534E] md:table-cell">{date(f.created_at)}</td>
      <td className="py-2.5"><div className="flex justify-end"><button className={tool} onClick={() => onDownload(f)} aria-label="Download" data-testid={`portal-file-${f.id}-download`}><Download className="h-4 w-4" /></button></div></td>
    </tr>
  );
};

export const PortalFiles = ({ onRequested }) => {
  const [folderId, setFolderId] = useState(null);
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState("");
  const load = useCallback(() => api.get("/portal/drive", { params: folderId ? { folder_id: folderId } : {} }).then((r) => setData(r.data)).catch((e) => { toast.error(errText(e)); setFolderId(null); }), [folderId]);
  useEffect(() => { load(); return undefined; }, [load]);
  const download = async (f) => {
    try {
      const r = await api.get(`/portal/files/${f.id}/download`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = f.name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } catch (e) { toast.error(errText(e)); }
  };
  const folders = Array.isArray(data?.folders) ? data.folders : [];
  const files = Array.isArray(data?.files) ? data.files : [];
  const done = () => { load(); onRequested?.(); };
  const reqBtn = "flex items-center gap-2 px-4 py-2.5 text-sm font-bold whitespace-nowrap";

  return (
    <div data-testid="portal-files">
      {data?.can_request && (
        <div className="flex flex-wrap justify-end gap-2">
          <button onClick={() => setDialog("folder")} className={`${reqBtn} border border-black/80 hover:bg-black hover:text-white`} data-testid="portal-request-folder"><FolderPlus className="h-4 w-4" />Request new folder</button>
          <button onClick={() => setDialog("file")} className={`${reqBtn} bg-[#0A0A0A] text-white hover:bg-[#E62314]`} data-testid="portal-request-file"><FilePlus2 className="h-4 w-4" />Request new file</button>
        </div>
      )}
      <div className="mt-4 border border-black/15 bg-white shadow-sm">
        <nav className="flex flex-wrap items-center gap-1 border-b border-black/10 bg-[#F3F2EF] px-4 py-2.5 text-sm" data-testid="portal-breadcrumb">
          <button onClick={() => setFolderId(null)} className="flex items-center gap-1.5 rounded px-2 py-1 font-semibold hover:bg-black/10"><HardDrive className="h-4 w-4" />Files</button>
          {(data?.path || []).map((p) => <span key={p.id} className="flex items-center gap-1"><ChevronRight className="h-4 w-4 text-[#57534E]" /><button onClick={() => setFolderId(p.id)} className="rounded px-2 py-1 font-semibold hover:bg-black/10">{p.name}</button></span>)}
          {data?.folder && <span className="ml-auto text-xs text-[#57534E]" data-testid="portal-folder-level">{levelOf(data.folder.level).label}</span>}
        </nav>
        <div className="min-h-[360px] p-4 sm:p-6">
          {!data && <div className="h-32 animate-pulse bg-black/5" />}
          {folders.length > 0 && (
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8" data-testid="portal-folders">
              {folders.map((f) => (
                <button key={f.id} onClick={() => setFolderId(f.id)} className="group flex flex-col items-center rounded p-2 text-center hover:bg-[#E8F0FE]" data-testid={`portal-folder-${f.id}`}>
                  <FolderIcon className="h-16 w-20 transition-transform group-hover:-translate-y-0.5" />
                  <span className="mt-1 w-full break-words text-sm font-semibold [hyphens:none]">{f.name}</span>
                  <span className="text-[11px] text-[#57534E]">{f.count ?? 0} {f.count === 1 ? "item" : "items"}</span>
                </button>
              ))}
            </div>
          )}
          {files.length > 0 && (
            <table className="mt-6 w-full text-left text-sm" data-testid="portal-files-table">
              <thead className="border-b border-black/10 text-xs uppercase tracking-wider text-[#57534E]">
                <tr><th className="py-2 font-semibold">Name</th><th className="hidden py-2 font-semibold sm:table-cell">Size</th><th className="hidden py-2 font-semibold md:table-cell">Added</th><th /></tr>
              </thead>
              <tbody>{files.map((f) => <FileRow key={f.id} f={f} showLevel={!data?.folder} onDownload={download} />)}</tbody>
            </table>
          )}
          {data && !folders.length && !files.length && (
            <div className="flex min-h-[300px] flex-col items-center justify-center text-center text-[#57534E]" data-testid="portal-empty">
              <FolderIcon className="h-20 w-24 opacity-60" />
              <p className="mt-4 text-base font-semibold text-[#0A0A0A]">{data.folder ? "This folder is empty" : "Nothing here yet"}</p>
            </div>
          )}
        </div>
      </div>
      <FolderRequest open={dialog === "folder"} onOpenChange={(o) => setDialog(o ? "folder" : "")} parent={data?.folder} onDone={done} />
      <FileRequest open={dialog === "file"} onOpenChange={(o) => setDialog(o ? "file" : "")} folder={data?.folder} onDone={done} />
    </div>
  );
};
